import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const missingFactors = [
  "Where you would actually live",
  "How healthcare works in practice",
  "Whether you can realistically stay long-term",
  "What daily life would feel like once you’re there",
];

const actualDecisionFactors = [
  "Cost you can sustain",
  "Healthcare you can rely on",
  "A residency path you can follow",
  "A lifestyle that matches your day-to-day expectations",
];

const renderBulletList = (items, prefix) => (
  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid={`${prefix}-list`}>
    {items.map((item, index) => (
      <li key={item} className="flex gap-3" data-testid={`${prefix}-item-${index + 1}`}>
        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function WhyCostToRetireAbroadChartsAreMisleading() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="cost-chart-guide-page">
      <SeoArticleSchema
        headline="Why “Cost to Retire Abroad” Charts Don’t Tell the Whole Story"
        description="Cost-of-living charts can mislead your retirement decision abroad. Learn how to judge real affordability, tradeoffs, and fit before you commit."
        pathname="/insights/why-cost-to-retire-abroad-charts-are-misleading"
        keywords={[
          "cost to retire abroad charts",
          "retire abroad cost comparison",
          "why country cost charts mislead",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="cost-chart-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="cost-chart-hero-accent" />
            <p className="rc-section-kicker mb-4" data-testid="cost-chart-hero-kicker">Decision guide</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="cost-chart-page-title">
              Why “Cost to Retire Abroad” Charts Don’t Tell the Whole Story
            </h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="cost-chart-page-intro">
              <p>Most “cost to retire abroad” charts look like they answer the question.</p>
              <p className="text-stone-900">They don’t.</p>
              <p>They show broad averages—but not how a country will actually work for your life.</p>
              <p data-testid="cost-chart-page-role-note">
                This page is about oversimplification. If you are still broadly deciding between countries, start with{" "}
                <Link
                  to="/insights/what-country-should-i-move-to"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="cost-chart-what-country-link"
                >
                  what actually determines country fit
                </Link>
                .
              </p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="cost-chart-key-takeaway"
            paragraphs={[
              "Headline cost-to-retire-abroad charts are useful for rough orientation, but they are too simple to answer whether a country will work for your life.",
              "A realistic budget decision depends on where you would live, what kind of healthcare you expect, whether the residency path is workable, and how much day-to-day convenience matters to you.",
            ]}
          />

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="cost-chart-image-section">
            <h3
              className="mx-auto mb-3 w-[calc(100vw-48px)] max-w-[520px] font-manrope text-xl font-semibold leading-tight text-stone-900 sm:text-2xl"
              data-testid="cost-chart-image-header"
            >
              What it costs to retire COMFORTABLY → by country:
            </h3>
            <div className="mx-auto w-[calc(100vw-48px)] max-w-[560px] rounded-[1rem] bg-[#efe7da] px-5 py-5 sm:px-6 sm:py-6" data-testid="cost-chart-image-figure">
              <div className="mx-auto w-full max-w-[520px]" data-testid="cost-chart-image-frame">
                <img
                  src="/images/cost-chart-cropped-v3.png"
                  alt="Example of a cropped cost to retire abroad chart"
                  className="mx-auto block h-auto w-full rounded-[4px]"
                  data-testid="cost-chart-guide-image"
                />
              </div>
              <p className="mx-auto mt-5 w-full max-w-[520px] text-sm leading-7 text-stone-500 sm:mt-6" data-testid="cost-chart-image-caption">
                Example of a typical “cost to retire abroad” chart. Useful for rough comparisons, but it leaves out key factors that determine whether a country actually fits your situation.
              </p>
            </div>
            <p className="mt-4 text-lg leading-8 text-stone-900" data-testid="cost-chart-image-takeaway">
              At a glance, this looks like enough to make a decision.<br />But it’s not.
            </p>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="cost-chart-context-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="cost-chart-why-cost-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="cost-chart-why-cost-title">Why does cost alone break down so quickly?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="cost-chart-why-cost-copy">
                <p>A number like “$32,000 per year in Portugal” sounds clear.</p>
                <p>But that same budget could feel comfortable in a smaller town—and tight in Lisbon.</p>
                <p>Cost changes based on where you live, how you live, and what you expect day to day.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="cost-chart-missing-factors-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="cost-chart-missing-factors-title">What do these charts leave out?</h2>
              {renderBulletList(missingFactors, "cost-chart-missing-factors")}
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="cost-chart-missing-factors-copy">
                These are the factors that shape your experience.
              </p>
            </article>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="cost-chart-decision-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="cost-chart-information-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="cost-chart-information-title">Why isn’t general information enough to choose a country?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="cost-chart-information-copy">
                <p>Cost charts help you compare countries in general.</p>
                <p>They don’t tell you which country fits your priorities, your budget, and your situation.</p>
                <p>That’s where most people get stuck.</p>
                <p>
                  A direct comparison like{" "}
                  <Link
                    to="/insights/portugal-vs-spain-living-abroad"
                    className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                    data-testid="cost-chart-portugal-spain-link"
                  >
                    Portugal vs Spain: Which Country Fits Your Priorities?
                  </Link>
                  {" "}can make those tradeoffs easier to see.
                </p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="cost-chart-what-matters-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="cost-chart-what-matters-title">What should matter more than the headline number?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="cost-chart-what-matters-copy-1">
                A strong decision comes from balancing:
              </p>
              {renderBulletList(actualDecisionFactors, "cost-chart-actual-decision-factors")}
              <p className="mt-5 text-base leading-8 text-stone-800" data-testid="cost-chart-what-matters-copy-2">
                No chart combines those for you.
              </p>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="cost-chart-clarity-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="cost-chart-clarity-title">What should you do once the chart stops helping?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="cost-chart-clarity-copy">
              <p>Relocation Clarity helps you compare countries based on your priorities—not just averages.</p>
              <p>Instead of guessing from general numbers, you can see which countries actually fit your situation before you commit.</p>
              <p>
                If you are still narrowing the field, start with{" "}
                <Link
                  to="/insights/create-country-shortlist-for-moving-abroad"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="cost-chart-shortlist-link"
                >
                  a realistic shortlist
                </Link>
                .
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Where to go next"
            title="Budget becomes more useful when you place it inside the bigger decision."
            description="If this page helped you move beyond headline numbers, the next useful step is usually to reconnect budget with overall fit, shortlist structure, and a clearer view of what the Blueprint actually looks like."
            testIdPrefix="cost-chart-next-steps"
            items={[
              {
                slug: "fit-guide",
                eyebrow: "Reconnect the full decision",
                title: "See what actually determines country fit",
                description: "Bring budget back into the wider decision around healthcare, residency practicality, and daily life.",
                ctaLabel: "Read the decision guide",
                to: "/insights/what-country-should-i-move-to",
              },
              {
                slug: "shortlist",
                eyebrow: "Use a clearer filter",
                title: "Build a realistic shortlist",
                description: "Use the budget question to remove weaker options and focus on countries still worth comparing.",
                ctaLabel: "Open the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid output",
                title: "Preview a Sample Blueprint",
                description: "See how cost, tradeoffs, and next steps are organized when the decision gets more personalized.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="cost-chart-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" data-testid="cost-chart-cta-accent" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300" data-testid="cost-chart-cta-kicker">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50" data-testid="cost-chart-cta-title">Want to pressure-test affordability against real life?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300" data-testid="cost-chart-cta-copy">
              The assessment compares countries through budget realism, healthcare confidence, residency practicality, and the tradeoffs that change what looks affordable on paper.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="cost-chart-cta-start-assessment-button">
                <Link to="/auth">
                  Start your assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}